import styled from 'styled-components';
import {sanitizeMessageHtml} from '../utils/sanitizeMessageHtml';
import {rewriteInlineImageSrcs} from '../utils/rewriteInlineImageSrcs';
import {ticketingAttachmentDownloadUrl} from '../../../graphql/forecastRelay';

export type StreamEntryType = 'email-sent' | 'email-received' | 'internal-note';

export interface StreamEntry {
	id: string;
	type: StreamEntryType;
	actorName: string;
	actorInitial: string;
	body: string;
	bodyHtml?: string | null;
	createdAt: string;
}

function formatTimestamp(iso: string): string {
	return new Date(iso).toLocaleString();
}

const Card = styled.div`
	margin-bottom: 16px;
`;

const Row = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;

const Avatar = styled.span<{$received: boolean}>`
	display: flex;
	height: 32px;
	width: 32px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 600;
	background: ${p => (p.$received ? '#dcfce7' : '#dbeafe')};
	color: ${p => (p.$received ? '#15803d' : '#1d4ed8')};
`;

const Main = styled.div`
	min-width: 0;
	flex: 1 1 0%;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const ActorName = styled.span`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 600;
	color: #0f172a;
`;

const CustomerBadge = styled.span`
	border-radius: 4px;
	padding: 2px 6px;
	font: ${p => p.theme.ticketing.font.overline};
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	background: #dcfce7;
	color: #15803d;
`;

const Timestamp = styled.span`
	margin-left: auto;
	font: ${p => p.theme.ticketing.font.body3};
	color: #94a3b8;
`;

const Bubble = styled.div`
	margin-top: 8px;
	border-radius: 6px;
	border: 1px solid #f1f5f9;
	background: #ffffff;
	padding: 12px 16px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #334155;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const BodyText = styled.p`
	margin: 0;
	white-space: pre-wrap;
`;

function RoleBadge({type}: {type: StreamEntryType}) {
	if (type === 'email-received') {
		return <CustomerBadge>Customer</CustomerBadge>;
	}
	return null;
}

interface Props {
	entry: StreamEntry;
}

export function StreamHistoryCard({entry}: Props) {
	const received = entry.type === 'email-received';

	return (
		<Card data-testid="stream-history-card">
			<Row>
				<Avatar $received={received}>{entry.actorInitial}</Avatar>
				<Main>
					<Header>
						<ActorName>{entry.actorName}</ActorName>
						<RoleBadge type={entry.type} />
						<Timestamp data-testid="stream-card-timestamp">{formatTimestamp(entry.createdAt)}</Timestamp>
					</Header>
					<Bubble>
						{entry.bodyHtml != null ? (
							<div
								dangerouslySetInnerHTML={{
									__html: rewriteInlineImageSrcs(
										sanitizeMessageHtml(entry.bodyHtml),
										ticketingAttachmentDownloadUrl
									),
								}}
							/>
						) : (
							<BodyText>{entry.body}</BodyText>
						)}
					</Bubble>
				</Main>
			</Row>
		</Card>
	);
}
